import { 
    Button,
    Flex, 
    Modal, 
    ModalBody, 
    ModalCloseButton, 
    ModalContent, 
    ModalFooter, 
    ModalHeader, 
    ModalOverlay, 
    Stack, 
    Text 
} from "@chakra-ui/react";
import { Input } from "../../components/Forms/Input";
import { Select } from "../../components/Forms/Select";
import { StandInfo } from "../../components/Forms/StandInfo";
import { Balance } from "../../models/Transfer";
import { MinusCircle } from "../../styles/icons/MinusCircle";
import { PlusCircle } from "../../styles/icons/PlusCircle";
import { formatCurrency, formatCurrencyToApi, showToast } from "../../utils";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useTransfer } from "../../hooks/useTransfer";
import { InputCurrency } from "../../components/Forms/InputCurrency";

interface OperationModalProps {
    isOpen: boolean;
    onClose: () => void;
    type?: string;
    seller: Balance;
}

export interface OperationData {
    amount: string;
    type: string;
    description: string;
}

const createOperationSchema = yup.object().shape({
    amount: yup.string().required("Preenchimento obrigatório!"),
    type: yup.string().required("Preenchimento obrigatório!"),
    description: yup.string(),
});

const DEBIT_TYPES = [
    {
        name: "Parcela de antecipação",
        value: "INSTALLMENTS_ANTICIPATION",
    },
    {
        name: "Correção",
        value: "DEBIT_CORRECTION",
    },
]
const CREDIT_TYPES = [
    {
        name: "Antecipação de receita",
        value: "REVENUE_ANTICIPATION",
    },
    {
        name: "Desconto em taxas",
        value: "FEE_DISCOUNT",
    },
    {
        name: "Correção",
        value: "CREDIT_CORRECTION",
    },
]

export function OperationModal({ isOpen, onClose, type = "", seller}: OperationModalProps) {
    const {
        register,
        handleSubmit,
        setValue,
        watch
      } = useForm({
        resolver: yupResolver(createOperationSchema),
    });
    const { postTransfer } = useTransfer();
    const selectedType = watch("type");
    const onSubmit = handleSubmit(async ({amount, type, description}: OperationData) => {
        if(amount === "R$ 0,00") return
        try {
            const operationData = {
                idSeller: seller?.sellerAccountancy?.id,
                amount: formatCurrencyToApi(amount),
                type,
                description: (selectedType === "CREDIT_CORRECTION" || selectedType === "DEBIT_CORRECTION" ) ? description : "",
            }
            await postTransfer(operationData);
            showToast({ message: "Sucesso ao criar a operação", type: "success" });
        } catch (error: any) {
            error?.response?.data?.errors?.map((error: any) => (
                showToast({
                  type: "error",
                  message: `${error?.code} - ${error?.description}`
                })
            ));
        } finally {
            handleClearFunction();
        }
    });

    const handleClearFunction = () => {
        setValue("description", "");
        setValue("amount", "");
        onClose();
    }

    return (
        <Modal isOpen={isOpen} onClose={handleClearFunction}>
            <ModalOverlay />
            <ModalContent  as="form" onSubmit={onSubmit}>
                <ModalHeader>
                    <Flex 
                        w="100%" 
                        maxH={56} 
                        align="center"
                        gap="2"
                    >
                        {type === "CREDIT" 
                        ? 
                        <PlusCircle
                            color="#52C41A"
                            size={15} 
                        /> 
                        : 
                        <MinusCircle 
                            color="#FF4D4F" 
                            size={15} 
                        />}
                        <Text 
                            fontWeight={500}
                            color="black"
                            fontSize="1rem"
                        >
                            {type === "CREDIT" ? "Criar operação de Crédito" : "Criar operação de Débito"}
                        </Text>
                        <ModalCloseButton />
                    </Flex>
                </ModalHeader>
                <ModalBody p="24px" bgColor="gray.25">
                    <Flex w="100%" direction="column" gap="2">
                        <StandInfo 
                            label="Cliente"
                            value={seller?.sellerAccountancy?.sellerName}
                        />
                        <StandInfo 
                            label="Saldo atual"
                            value={formatCurrency({value: seller?.amount})}
                        />
                    </Flex>
                </ModalBody>
                <ModalFooter p="24px">
                    <Stack
                        w="100%"
                        spacing="2"
                    >
                        <InputCurrency 
                            name="amount"
                            register={{...register("amount")}}
                            label={type === "CREDIT" ? "Valor do Crédito" : "Valor do Débito"}
                            labelColor="black"
                            mask="currency"
                            placeholder="R$ 0,00"
                            setValue={setValue}
                        />
                        <Select
                            label="Tipo"
                            labelColor="black"
                            {...register("type")}
                        >
                            {type === "DEBIT" ? 
                                DEBIT_TYPES.map((debit) => <option value={debit.value}>{debit.name}</option>) : 
                                CREDIT_TYPES.map((credit)  => <option value={credit.value}>{credit.name}</option>
                            )}
                        </Select>
                        {(selectedType === "CREDIT_CORRECTION" || selectedType === "DEBIT_CORRECTION") && (
                            <Input 
                                name="description"
                                register={{...register("description")}}
                                label="Título"
                                labelColor="black"
                            />
                        )} 
                    </Stack>
                </ModalFooter>
                <Flex 
                    w="100%" 
                    borderTop="1px solid" 
                    borderColor="gray.50"
                    p="2"
                    justifyContent="flex-end"
                    gap="2"
                >
                    <Button  
                        variant="secondary"
                        onClick={onClose}
                    >
                        Cancelar
                    </Button>
                    <Button 
                        variant="primary"
                        type="submit"
                    >
                        Confirmar
                    </Button>
                </Flex>
                
            </ModalContent>
        </Modal>
    )
}
